/**
 * lib/okta.ts — Okta SSO Configuration
 *
 * Singleton OktaAuth instance for Authorization Code + PKCE flow.
 * Used by login page and callback handler.
 */

import { OktaAuth } from '@okta/okta-auth-js';

// Okta configuration from environment variables
const oktaConfig = {
  issuer: import.meta.env.VITE_OKTA_ISSUER || '',
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID || '',
  redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI || '',
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true,
  tokenManager: {
    storage: 'memory' as const,
  },
};

// Singleton OktaAuth instance
let oktaAuthInstance: OktaAuth | null = null;

/**
 * Get or create the OktaAuth instance.
 * Returns null if Okta is not configured.
 */
export function getOktaAuth(): OktaAuth | null {
  if (typeof window === 'undefined') {
    return null; // Server-side, return null
  }

  if (!oktaConfig.issuer || !oktaConfig.clientId) {
    return null; // Okta not configured
  }

  if (!oktaAuthInstance) {
    oktaAuthInstance = new OktaAuth(oktaConfig);
  }

  return oktaAuthInstance;
}

/**
 * Check if Okta SSO is configured and available.
 */
export function isOktaConfigured(): boolean {
  return !!(oktaConfig.issuer && oktaConfig.clientId);
}

/**
 * Initiate Okta login redirect.
 */
export async function loginWithOkta(): Promise<void> {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) {
    throw new Error('Okta is not configured');
  }
  await oktaAuth.signInWithRedirect();
}

/**
 * Handle Okta callback and exchange authorization code for tokens.
 * Returns the access token if successful.
 */
export async function handleOktaCallback(): Promise<{ accessToken: string; user: OktaUser } | null> {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) {
    return null;
  }

  try {
    // Parse tokens from callback URL
    const tokenResponse = await oktaAuth.token.parseFromUrl();

    // Store tokens in Okta's token manager
    oktaAuth.tokenManager.setTokens(tokenResponse.tokens);

    // Get access token
    const accessToken = tokenResponse.tokens.accessToken?.accessToken;
    if (!accessToken) {
      throw new Error('No access token received');
    }

    // Get user info from ID token
    const idToken = tokenResponse.tokens.idToken;
    const user: OktaUser = {
      email: idToken?.claims?.email as string || '',
      name: idToken?.claims?.name as string || '',
      sub: idToken?.claims?.sub as string || '',
    };

    return { accessToken, user };
  } catch (error) {
    console.error('Okta callback error:', error);
    throw error;
  }
}

/**
 * Get current access token from Okta token manager.
 * Returns null if not authenticated or token expired.
 */
export async function getOktaAccessToken(): Promise<string | null> {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) {
    return null;
  }

  try {
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    if (accessToken && 'accessToken' in accessToken && !oktaAuth.tokenManager.hasExpired(accessToken)) {
      return accessToken.accessToken;
    }

    // Try to refresh
    const newToken = await oktaAuth.tokenManager.renew('accessToken');
    if (newToken && 'accessToken' in newToken) {
      return newToken.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Logout from Okta.
 */
export async function logoutFromOkta(): Promise<void> {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) {
    return;
  }

  try {
    await oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Okta logout error:', error);
    // Clear tokens locally even if signOut fails
    oktaAuth.tokenManager.clear();
  }
}

/**
 * Check if user is authenticated with Okta.
 */
export async function isOktaAuthenticated(): Promise<boolean> {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) {
    return false;
  }

  return oktaAuth.isAuthenticated();
}

// Type for Okta user info
export interface OktaUser {
  email: string;
  name: string;
  sub: string;
}
